import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FiUserPlus, FiSearch, FiX, FiTrash2 } from "react-icons/fi";
import Header from "../component/Header";
import { API_BASE_URL } from "../utils/apiConfig";

function DeviceUsersListPage() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const userRole = localStorage.getItem("role");
    const [searchQuery, setSearchQuery] = useState("");

    const displayedUsers = users.filter(user => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            user.role?.toLowerCase().includes(query) ||
            user.device_name?.toLowerCase().includes(query) ||
            String(user.device_id_FK)?.toLowerCase().includes(query)
        );
    });

    const handleDelete = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this device user?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_BASE_URL}/api/register/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(prev => prev.filter(u => u.id !== userId));
        } catch (err) {
            console.error("Failed to delete user", err);
            alert("Error deleting user.");
        }
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    navigate("/");
                    return;
                }
                const response = await axios.get(`${API_BASE_URL}/api/register/users`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.status) {
                    setUsers(response.data.data);
                }
            } catch (err) {
                console.error("Failed to fetch device users", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [navigate]);

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center">Loading Device Users...</div>;

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            <div className="grid grid-cols-1 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg">
                    <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                        <h2 className="text-xl font-bold text-green-400">Device Users List</h2>

                        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
                            {/* Search bar */}
                            <div className="relative flex items-center h-9">
                                <FiSearch size={13} className="absolute left-3 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search device users..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-full bg-black/40 border border-white/5 text-gray-300 text-[11px] font-bold rounded-xl pl-8 pr-8 focus:outline-none focus:ring-1 focus:ring-green-500 transition w-48 md:w-64 placeholder:text-gray-600"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-3 text-gray-500 hover:text-red-400 transition"
                                    >
                                        <FiX size={13} />
                                    </button>
                                )}
                            </div>

                            {userRole === 'superadmin' && (
                                <button
                                    onClick={() => navigate("/addDeviceUser")}
                                    className="flex items-center gap-2 px-4 h-9 text-sm bg-green-400 font-semibold rounded-xl text-black hover:bg-green-500 transition shadow-lg shadow-green-900/20 active:scale-95 shrink-0"
                                >
                                    <FiUserPlus size={16} /> Add Device User
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-gray-300">
                            <thead>
                                <tr className="border-b border-gray-700 text-gray-500 uppercase tracking-wider text-[11px]">
                                    <th className="px-4 py-3 text-left">Role</th>
                                    <th className="px-4 py-3 text-left">Device Name</th>
                                    <th className="px-4 py-3 text-left">Device ID</th>
                                    <th className="px-4 py-3 text-left">Created At</th>
                                    {userRole === 'superadmin' && <th className="px-4 py-3 text-left">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {displayedUsers.map((user) => (
                                    <tr key={user.id} className="border-b border-gray-800 hover:bg-[#1e1e1e] transition">
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase border border-green-400 text-green-400">
                                                {user.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-400">{user.device_name}</td>
                                        <td className="px-4 py-3">
                                            <span className="px-3 py-1 text-[11px] font-semibold rounded-full bg-gray-900 border border-gray-800 text-gray-400">
                                                ID: {user.device_id_FK}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">
                                            {new Date(user.created_at).toLocaleDateString()}
                                        </td>
                                        {userRole === 'superadmin' && (
                                            <td className="px-4 py-3 text-left">
                                                <button onClick={() => handleDelete(user.id)} className="text-red-500 hover:text-red-400 p-1 bg-red-500/10 hover:bg-red-500/20 rounded transition" title="Delete Device User">
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                {displayedUsers.length === 0 && (
                                    <tr>
                                        <td colSpan={userRole === 'superadmin' ? "6" : "5"} className="px-4 py-4 text-center text-gray-500 italic">
                                            No device users found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DeviceUsersListPage;
